import styled from 'styled-components';
import { Icon } from '../Icon/Icon';
import { IconKey } from '../SvgIcons/svgIconsParams';
import { SvgIcon } from '../SvgIcons/svgIcons';
import { theme } from '../theme';

const IconButtonElement = styled.button<ICommonProps>`
  &&.disabled,
  &&.disabled:hover {
    color: ${({ theme }) => theme.vars.newColor.darkGS3};
    background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
    border: ${({ theme }) => `1px solid ${theme.vars.newColor.lightGS2}`};
    cursor: not-allowed;
  }
  &&:hover {
    background-color: ${({ theme, isActive }) => !isActive && theme.vars.newColor.lightGS2};
    border: ${({ theme, isActive }) => !isActive && `1px solid ${theme.vars.newColor.lightGS1}`};
  }
  &&:active {
    color: ${({ theme }) => theme.vars.newColor.white};
    background-color: ${({ theme }) => theme.designToken.primary300};
    border: ${({ theme }) => `1px solid ${theme.designToken.primary300}`};
    transition: none;
  }
  box-shadow: none;
  font-weight: normal;
  text-align: center;
  display: flex;
  justify-content: center;
  cursor: pointer;
  width: ${({ theme }) => theme.designToken.spacing4};
  height: ${({ theme }) => theme.designToken.spacing4};
  color: ${({ theme, isActive }) => (isActive ? theme.designToken.baseWhite : theme.designToken.neutral700)};
  background-color: ${({ theme, isActive }) => (isActive ? theme.designToken.primary300 : 'transparent')};
  border: 1px solid transparent;
  flex-direction: column;
  align-items: center;
  border-radius: ${({ isActive }) => (isActive ? '4px' : '0')};
`;

interface ICommonProps {
  selected?: boolean;
  /** Button type.  Defaults to "button" */
  type?: 'button' | 'reset' | 'submit';
  /** The color of the background on the button.  */
  backgroundColor?: string;
  isActive?: boolean;
}

type Variation = 'button' | 'div';

interface IProps extends ICommonProps {
  /** Name of the icon */
  icon: IconKey | string;
  /** The color of the icon.  */
  color?: string;
  /** Whether to disable the Icon button or not.  Defaults to false. */
  disabled?: boolean;
  /** Called when button is clicked with the event passed in. */
  onClick?: (e?: React.MouseEvent) => void;
  /** onChange invoked whenever the IconButton is changed */
  onChange?: (checked?: boolean) => void;
  /** Added to meet WCAG 2 AA- Gives the icon component a label as it has no value */
  ariaLabel: string;
  /** Optional prop to change the semantic element to a div instead of a button. Defaults to button. */
  htmlTag?: Variation;
  /** Called when we want to use new svg icons. */
  svgIcon?: boolean;
}

export const IconButton = (props: IProps) => {
  const handleChange = () => {
    if (props.onChange) {
      props.onChange(!props.selected);
    }
  };

  return (
    <IconButtonElement
      data-testid="IconButton"
      {...props}
      backgroundColor={props.backgroundColor || null}
      className={props.disabled && 'disabled'}
      onClick={props.onClick}
      aria-label={props.ariaLabel}
      onChange={handleChange}
      type={props.type || 'button'}
      as={props.htmlTag}
      isActive={props.isActive}
    >
      {props.svgIcon ? (
        <SvgIcon
          name={props.icon as IconKey}
          color={props.isActive ? theme.designToken.baseWhite : props.color}
          size="24"
        />
      ) : (
        <Icon icon={props.icon} color={props.color} />
      )}
    </IconButtonElement>
  );
};
